import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Link2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { copyCode } from "@/lib/clipboard";
import { FORMAT_LABELS, PLATFORM_FIELDS, PLATFORM_LABELS, media } from "@/lib/smm";

const inputCls = "w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:border-emerald-500 outline-none";

// Short player: shows a message + Retry (reloads the src) when the video fails to load.
const ShortVideo = ({ src, poster }) => {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => { setFailed(false); setAttempt(0); }, [src]);
  const url = attempt ? `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}` : src;
  return (
    <>
      <video key={url} src={url} poster={poster} controls muted playsInline preload="metadata" onError={() => setFailed(true)} className="w-full bg-slate-100" data-testid="smm-preview-video" />
      {failed && (
        <div className="px-3 py-2 text-xs text-red-700 bg-red-50 flex items-center justify-between gap-2" data-testid="smm-preview-video-error">
          <span>This video could not be loaded. Try again in a minute.</span>
          <button onClick={() => { setFailed(false); setAttempt((n) => n + 1); }} className="shrink-0 font-bold px-2 py-1 rounded border border-red-200 hover:bg-red-100" data-testid="smm-preview-video-retry">Retry</button>
        </div>
      )}
    </>
  );
};

// Mock frame so the admin sees roughly how each platform shows the post.
const Frame = ({ platform, img, video, text }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${platform === "youtube" ? "max-w-[260px]" : ""}`} data-testid={`smm-preview-frame-${platform}`}>
    <div className="px-3 py-2 text-xs font-bold text-slate-500 border-b border-slate-100">{PLATFORM_LABELS[platform]} preview</div>
    {video ? <ShortVideo src={video} poster={img} />
      : <img src={img} alt="" className="w-full bg-slate-100" />}
    <pre className="p-3 text-xs whitespace-pre-wrap font-sans text-slate-700 max-h-64 overflow-y-auto" data-testid={`smm-preview-text-${platform}`}>{text}</pre>
  </div>
);

export const PreviewDialog = ({ item, platformFormat, onClose, onSaved }) => {
  const [tab, setTab] = useState("instagram");
  const [form, setForm] = useState({});
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) {
      setForm(item.content || {});
      setTags((item.hashtags || []).join(" "));
    }
  }, [item]);
  if (!item) return null;
  const fmt = platformFormat[tab];
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/smm/items/${item.id}`, { content: form, hashtags: tags.split(/[\s,]+/).filter(Boolean) });
      toast.success("Saved - please review and approve again");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="smm-preview-dialog">
        <DialogHeader><DialogTitle className="pr-6">{item.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1.5" role="tablist">
          {Object.keys(PLATFORM_LABELS).map((p) => (
            <button key={p} role="tab" aria-selected={tab === p} onClick={() => setTab(p)} data-testid={`smm-preview-tab-${p}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${tab === p ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{PLATFORM_LABELS[p]}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            {PLATFORM_FIELDS[tab].map(([k, label]) => (
              <label key={k} className="block space-y-1">
                <span className="text-xs font-semibold text-slate-600">{label}</span>
                <textarea rows={k.endsWith("title") ? 2 : 5} className={inputCls} value={form[k] || ""} onChange={set(k)} data-testid={`smm-edit-${k}`} />
              </label>
            ))}
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600">Headline on the design</span>
              <input className={inputCls} value={form.headline || ""} onChange={set("headline")} data-testid="smm-edit-headline" />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600">Hindi line</span>
              <input className={inputCls} value={form.hindi_line || ""} onChange={set("hindi_line")} data-testid="smm-edit-hindi_line" />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600">Hashtags</span>
              <input className={inputCls} value={tags} onChange={(e) => setTags(e.target.value)} data-testid="smm-edit-hashtags" />
            </label>
            <p className="text-[11px] text-slate-500">The tracked link and the affiliate disclosure are added automatically - you don't need to type them. Saving sends the item back to review.</p>
            {item.posts?.some((p) => ["scheduled", "due"].includes(p.status)) && (
              <p className="text-[11px] text-amber-800 bg-amber-50 rounded-lg p-2" data-testid="smm-edit-scheduled-note">Posts already in the calendar keep the caption they were scheduled with - edit them in Calendar & queue if needed.</p>
            )}
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2 rounded-lg disabled:opacity-60" data-testid="smm-edit-save"><Save size={16} /> {busy ? "Saving…" : "Save changes"}</button>
          </div>
          <div className="space-y-3">
            <Frame platform={tab} img={media(item.formats[fmt])} video={tab === "youtube" && item.short_url ? media(item.short_url) : null} text={item.previews?.[tab] || "Write captions first."} />
            {tab === "youtube" && <p className="text-[11px] text-slate-500" data-testid="smm-short-note">Press play to see the 12-second Short that will be uploaded (first play takes a few seconds while it's made).</p>}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => copyCode(item.previews?.[tab] || "", "Caption copied")} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="smm-preview-copy"><Copy size={14} /> Copy caption</button>
              <button onClick={() => copyCode(item.links?.[tab] || "", "Tracked link copied")} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="smm-preview-copy-link"><Link2 size={14} /> {tab === "pinterest" ? "Copy Pin destination link" : tab === "instagram" ? "Copy link for bio / story sticker" : "Copy tracked link"}</button>
              {Object.entries(FORMAT_LABELS).map(([f, label]) => (
                <a key={f} href={media(item.formats[f])} download={`${item.id}-${f}.jpg`} target="_blank" rel="noreferrer" data-testid={`smm-preview-download-${f}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Download size={14} /> {label}</a>
              ))}
              {item.short_url && (
                <a href={media(item.short_url)} download={`${item.id}-short.mp4`} target="_blank" rel="noreferrer" data-testid="smm-preview-download-short"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Download size={14} /> Shorts video MP4</a>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
